// Source unique de vérité pour le <head> de chaque route.
// Lue quatre fois :
//   - par SeoHead au render (navigation client)
//   - par le plugin prerender de vite.config.ts (émission d'un HTML par route)
//   - par scripts/prerender.mjs (injection du corps + génération du sitemap)
//   - par scripts/indexnow.mjs (notification Bing/Yandex après déploiement)
// Toute route ajoutée ici doit l'être aussi dans src/routes.tsx. Le sitemap, lui,
// est désormais généré à partir de ce fichier : il ne peut plus dériver.

import { PRICE_SOURCE_SHORT } from "./priceSources.ts";

export type RouteSeo = {
  title: string;
  description: string;
  url: string;
  image: string;
  imageAlt?: string;
  siteName?: string;
  twitterSite?: string;
  twitterCreator?: string;
  ogType?: 'website' | 'article' | 'profile' | 'video.other';
  /** Pose `robots: noindex`. Réservé à la page 404, qui ne doit jamais être indexée. */
  noindex?: boolean;
  /**
   * Date de dernière modification réelle du contenu (AAAA-MM-JJ), pour le sitemap.
   * À mettre à jour quand la page change vraiment : un lastmod systématiquement
   * daté d'aujourd'hui est ignoré par Google, qui y voit un signal bruité.
   */
  lastmod?: string;
  /** Priorité relative dans le sitemap. Défaut 0.9, la home est à 1.0. */
  priority?: number;
};

export const SITE = 'https://studiotvai.com';

/**
 * Titre de 60 caractères au plus (au-delà, Google le tronque, voire le réécrit) : on garde
 * « main + extra | StudioTV », puis on retire d'abord la marque, puis l'extra.
 */
export function seoTitle(main: string, extra = ''): string {
  const tries = [`${main}${extra} | StudioTV`, `${main}${extra}`, `${main} | StudioTV`, main];
  return tries.find((t) => t.length <= 60) ?? main;
}

/** La date la plus récente de plusieurs AAAA-MM-JJ (contenu, données…). */
export const latestDay = (...days: string[]) => [...days].sort().at(-1)!;
const COVER = `${SITE}/og-cover.png`;
// Un seul nom de site (og:site_name), celui du JSON-LD WebSite : « StudioTV ».
const BRAND = 'StudioTV';
const HANDLE = '@ytstudiotv';

export const base = { image: COVER, siteName: BRAND, twitterSite: HANDLE, twitterCreator: HANDLE };

export const ROUTE_SEO = {
  '/': {
    ...base,
    title: 'StudioTV: LLM VRAM Calculator, GPU Prices & AI Courses',
    description:
      'Free tools for AI engineers: LLM VRAM calculator, hourly GPU rental prices, and lessons on gradient descent, compilers, lakehouses and MCP.',
    url: `${SITE}/`,
    imageAlt: 'StudioTV: LLM VRAM calculator, GPU rental prices and AI courses',
    ogType: 'website',
    lastmod: '2026-07-29',
    priority: 1.0,
  },
  '/gradient-descent': {
    ...base,
    title: 'Interactive Gradient Descent Animation in 2D & 3D | StudioTV',
    description:
      'Learn gradient descent with interactive 2D and 3D animations: learning rate, convergence and overshoot, plus a neural network training playground.',
    url: `${SITE}/gradient-descent`,
    imageAlt: 'Interactive gradient descent visualizations in 2D and 3D',
    ogType: 'article',
    lastmod: '2026-10-06',
    priority: 0.9,
  },
  '/compiler': {
    ...base,
    // « StudioTV — Interactive Learning: Build Your Compiler (S0–S5) » : la marque d'abord, et un jargon (S0–S5)
    // que personne ne cherche.
    title: 'Build a Compiler Step by Step: Interactive Tutorial | StudioTV',
    description:
      'Build a tiny language compiler step by step: expressions, variables, control flow, functions, runtime I/O, then reach self‑hosting with a compiler written in S.',
    url: `${SITE}/compiler`,
    imageAlt: 'Build a self-hosting compiler step by step',
    ogType: 'article',
    lastmod: '2026-10-06',
    priority: 0.9,
  },
  '/lakehouse': {
    ...base,
    title: 'Lakehouse Tutorial: Kubernetes, MinIO, Trino & Iceberg',
    description:
      'Step-by-step tutorial: what makes a good lakehouse, then deploy one on Kubernetes with MinIO, Trino, Apache Iceberg and Airbyte, YAML manifests included.',
    url: `${SITE}/lakehouse`,
    imageAlt: 'Lakehouse on Kubernetes with MinIO, Trino and Apache Iceberg',
    ogType: 'article',
    lastmod: '2026-10-06',
    priority: 0.9,
  },
  '/mcp': {
    ...base,
    title: 'Model Context Protocol (MCP) Tutorial for AI Developers',
    description:
      'The Model Context Protocol (MCP) explained for AI developers: architecture, JSON-RPC transport, writing MCP servers and tool integration, with examples.',
    url: `${SITE}/mcp`,
    // og-mcp.png n'a jamais existé : nginx renvoyait index.html (soft 404), donc
    // l'aperçu social de /mcp était vide. On retombe sur le cover commun.
    imageAlt: 'StudioTV: LLM VRAM calculator, GPU rental prices and AI courses',
    ogType: 'website',
    lastmod: '2026-07-29',
    priority: 0.9,
  },
  '/llm-vram-calculator': {
    ...base,
    title: 'LLM VRAM Calculator: How Many GPUs to Run Any LLM | StudioTV',
    description:
      'How many GPUs do you need to run an LLM? Free VRAM calculator for Qwen, Gemma 4, gpt-oss, DeepSeek or any Hugging Face model: KV cache, FP8/GGUF, LoRA.',
    url: `${SITE}/llm-vram-calculator`,
    imageAlt: 'LLM VRAM Calculator: GPU memory estimator for large language models',
    siteName: 'StudioTV',
    ogType: 'website',
    lastmod: '2026-10-17',
    priority: 0.9,
  },
  '/gpu-prices': {
    ...base,
    title: 'GPU Rental Prices Today: H100, B200, RTX 4090 | StudioTV',
    description:
      `Cheapest on-demand price to rent each cloud GPU (H100, H200, RTX 4090…) across ${PRICE_SOURCE_SHORT}, checked hourly, with price history.`,
    url: `${SITE}/gpu-prices`,
    imageAlt: 'GPU rental prices compared across cloud providers',
    ogType: 'website',
    lastmod: '2026-10-13',
    priority: 0.8,
  },
  '/ai-ml-architect': {
    ...base,
    title: 'AI/ML Architect Course: MLOps, Distributed Training',
    description:
      'Complete AI/ML architect course covering distributed training, large-scale inference, MLOps, Kubernetes vs Slurm, platform design, security, and cost tradeoffs.',
    url: `${SITE}/ai-ml-architect`,
    imageAlt: 'AI/ML Architect Course by StudioTV',
    ogType: 'article',
    lastmod: '2026-10-06',
    priority: 0.9,
  },

  // Émise en 404.html par le plugin de prerender, servie par `error_page` dans
  // nginx.conf avec un vrai statut 404. Pas de route côté React Router : c'est le
  // catch-all `*` d'App.tsx qui l'affiche.
  '/404': {
    ...base,
    title: 'Page not found — StudioTV',
    description: 'This page does not exist. Browse the interactive experiences instead.',
    url: `${SITE}/404`,
    imageAlt: 'StudioTV: LLM VRAM calculator, GPU rental prices and AI courses',
    ogType: 'website',
    noindex: true,
  },
} satisfies Record<string, RouteSeo>;
